import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import SaveIcon from "@material-ui/icons/Save"
import SendIcon from "@material-ui/icons/Send"
import { useSnackbar } from "../../../utils/notificacoes"
import {
    obterDigiSac,
    salvarDigiSac,
    testarDigiSac,
    type IntegracaoDigiSac,
} from "../../../services/integracaoParceiros"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"

const inicial: IntegracaoDigiSac = {
    serviceId: "",
    urlPlataforma: "",
    tokenDigiSac: "",
    ativo: false,
}

export function IntegracaoDigiSacForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [dados, setDados] = useState<IntegracaoDigiSac>(inicial)
    const [telefone, setTelefone] = useState("")

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    const setCampo = <K extends keyof IntegracaoDigiSac>(campo: K, valor: IntegracaoDigiSac[K]) =>
        setDados(prev => ({ ...prev, [campo]: valor }))

    useEffect(() => {
        const carregar = async () => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const res = await obterDigiSac(autorizacao)
                if ("retornoErro" in res) {
                    alertStart(res.mensagem || "Erro ao carregar integração", "error")
                } else {
                    setDados({
                        serviceId: res.serviceId ?? "",
                        urlPlataforma: res.urlPlataforma ?? "",
                        tokenDigiSac: res.tokenDigiSac ?? "",
                        ativo: res.ativo,
                    })
                }
            } finally {
                setLoading(false)
            }
        }
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSalvar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        if (!dados.serviceId?.trim() || !dados.urlPlataforma?.trim() || !dados.tokenDigiSac?.trim()) {
            alertStart("Preencha Service ID, URL da Plataforma e Token.", "error")
            return
        }
        setLoading(true)
        try {
            const res = await salvarDigiSac(autorizacao, dados)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar", "error")
            } else {
                setDados({
                    serviceId: res.serviceId ?? "",
                    urlPlataforma: res.urlPlataforma ?? "",
                    tokenDigiSac: res.tokenDigiSac ?? "",
                    ativo: res.ativo,
                })
                alertStart("Configuração salva com sucesso!", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    const handleTestar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        if (!telefone.trim()) {
            alertStart("Informe um telefone para o teste.", "error")
            return
        }
        setLoading(true)
        try {
            const res = await testarDigiSac(autorizacao, telefone)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao enviar teste", "error")
            } else {
                alertStart(res.mensagem || "Mensagem de teste enviada!", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Integração DigiSac
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Parâmetros DigiSac
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Service ID"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.serviceId ?? ""}
                                    onChange={e => setCampo("serviceId", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="URL da Plataforma"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.urlPlataforma ?? ""}
                                    onChange={e => setCampo("urlPlataforma", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Token"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.tokenDigiSac ?? ""}
                                    onChange={e => setCampo("tokenDigiSac", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dados.ativo}
                                            onChange={e => {
                                                const checked = e.target.checked
                                                setCampo("ativo", checked)
                                            }}
                                            style={{ color: "#4caf50" }}
                                        />
                                    }
                                    label="Ativo"
                                />
                            </Grid>
                        </Grid>
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button
                                className={classes.saveButton}
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSalvar}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Área de testes
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Telefone"
                                    placeholder="Ex: 11999998888"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={telefone}
                                    onChange={e => setTelefone(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    className={classes.secondaryButton}
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SendIcon />}
                                    onClick={handleTestar}
                                >
                                    Enviar mensagem de teste
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
